<template>
  <div class="contents">
    <div class="container-fluid">
      <!-- Header -->
      <div class="row">
        <div class="col-lg-12">
          <div class="breadcrumb-main user-member justify-content-sm-between">
            <div class="d-flex flex-wrap justify-content-center breadcrumb-main__wrapper">
              <h4 class="text-capitalize fw-500 breadcrumb-title">Laporan Order (Halaman Publik)</h4>
            </div>
            <div class="action-btn">
              <button @click="exportCSV" class="btn btn-outline-success" style="margin:5px" :disabled="!orders.length">
                <i class="fas fa-file-csv"></i> Export CSV
              </button>
              <button @click="load" class="btn btn-secondary text-white" style="margin:5px">
                <i class="fas fa-sync"></i> Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="alert alert-info mb-3">
        <small>
          <i class="fas fa-info-circle"></i>
          Order yang dibuat dari halaman publik <b>/order</b> (checkout tanpa login). Status:
          <b class="text-success">success</b> = pembayaran lunas & paket aktif,
          <b style="color:#e65100">pending</b> = menunggu pembayaran,
          <b class="text-danger">failed</b> = pembayaran gagal/kedaluwarsa.
        </small>
      </div>

      <!-- Summary cards -->
      <div class="row" v-if="summary">
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card p-3" style="border-left:4px solid #1976d2">
            <div class="text-muted small">📋 TOTAL ORDER</div>
            <div class="h4 mb-0 font-weight-bold text-primary">{{ summary.total }}</div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card p-3" style="border-left:4px solid #2e7d32">
            <div class="text-muted small">✅ SUKSES</div>
            <div class="h4 mb-0 font-weight-bold" style="color:#2e7d32">{{ summary.success }}</div>
            <small class="text-muted">Pendapatan: {{ formatRupiah(summary.revenue) }}</small>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card p-3" style="border-left:4px solid #e65100">
            <div class="text-muted small">⏳ PENDING</div>
            <div class="h4 mb-0 font-weight-bold" style="color:#e65100">{{ summary.pending }}</div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card p-3" style="border-left:4px solid #c62828">
            <div class="text-muted small">❌ GAGAL</div>
            <div class="h4 mb-0 font-weight-bold" style="color:#c62828">{{ summary.failed }}</div>
          </div>
        </div>
      </div>

      <!-- Filter -->
      <div class="row mb-2">
        <div class="col-12">
          <div class="card p-3">
            <div class="d-flex align-items-center" style="gap:8px; flex-wrap:wrap">
              <span class="font-weight-bold">Status:</span>
              <button v-for="s in statusOptions" :key="s.val"
                      @click="setStatus(s.val)"
                      :class="['btn btn-sm', filter.status===s.val ? 'btn-primary':'btn-outline-primary']">
                {{ s.label }}
              </button>
              <span class="font-weight-bold" style="margin-left:16px">| Tanggal:</span>
              <input v-model="filter.date_from" @change="reload" type="date" class="form-control form-control-sm" style="width:150px" />
              <span>s/d</span>
              <input v-model="filter.date_to" @change="reload" type="date" class="form-control form-control-sm" style="width:150px" />
              <span class="font-weight-bold" style="margin-left:16px">| Cari:</span>
              <input v-model="filter.search" @input="onSearch" type="text" class="form-control form-control-sm" style="width:220px" placeholder="nama / email / no. HP" />
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="row">
        <div class="col-12">
          <div class="card global-shadow border bg-white radius-xl w-100 mb-30">
            <div v-if="isLoading" class="text-center p-4">
              <div class="spinner-border" role="status"></div>
              <p class="mt-2 text-muted">Loading...</p>
            </div>
            <div v-else-if="!orders.length" class="text-center text-muted p-4">
              <p>Belum ada order pada filter ini.</p>
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="bg-light">
                  <tr>
                    <th>#</th>
                    <th>Tanggal</th>
                    <th>Pembeli</th>
                    <th>Paket</th>
                    <th class="text-right">Nominal</th>
                    <th>Metode</th>
                    <th class="text-center">Status</th>
                    <th class="text-center" style="min-width:140px">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(r, i) in orders" :key="r.id">
                    <td>{{ (page - 1) * limit + i + 1 }}</td>
                    <td><small>{{ formatTime(r.created_at) }}</small></td>
                    <td>
                      <div class="font-weight-bold">{{ r.name || '-' }}</div>
                      <div><small style="font-family:monospace">{{ r.email || '-' }}</small></div>
                      <small class="text-muted">{{ r.phone || '-' }}</small>
                    </td>
                    <td><span class="badge badge-info">{{ r.package_name || '-' }}</span></td>
                    <td class="text-right font-weight-bold">{{ formatRupiah(r.nominal) }}</td>
                    <td><small>{{ r.method }}</small></td>
                    <td class="text-center">
                      <span :class="statusClass(r.status)">{{ statusLabel(r.status) }}</span>
                    </td>
                    <td class="text-center">
                      <button
                        v-if="r.status === 'pending' || r.status === 'failed'"
                        class="btn btn-sm btn-success mb-1"
                        :disabled="actionBusy === r.id"
                        @click="activateOrder(r)"
                        title="Aktifkan paket (mark lunas)"
                      >
                        <i class="fas fa-check"></i> Aktifkan
                      </button>
                      <button
                        class="btn btn-sm btn-outline-danger"
                        :disabled="actionBusy === r.id"
                        @click="deleteOrder(r)"
                        title="Hapus record order"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="orders.length" class="d-flex align-items-center justify-content-between p-3">
              <small class="text-muted">
                Menampilkan {{ (page - 1) * limit + 1 }}–{{ (page - 1) * limit + orders.length }} dari {{ totalRecords }} order
              </small>
              <div>
                <button class="btn btn-sm btn-outline-secondary" :disabled="page <= 1" @click="goPage(page - 1)">
                  <i class="fas fa-chevron-left"></i> Sebelumnya
                </button>
                <span class="mx-2">Hal {{ page }} / {{ totalPages }}</span>
                <button class="btn btn-sm btn-outline-secondary" :disabled="page >= totalPages" @click="goPage(page + 1)">
                  Berikutnya <i class="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      isLoading: false,
      summary: null,
      orders: [],
      totalRecords: 0,
      page: 1,
      limit: 25,
      filter: { status: "", date_from: "", date_to: "", search: "" },
      statusOptions: [
        { val: "", label: "Semua" },
        { val: "success", label: "Sukses" },
        { val: "pending", label: "Pending" },
        { val: "failed", label: "Gagal" },
      ],
      searchDebounce: null,
      actionBusy: null,
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalRecords / this.limit));
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    setStatus(s) {
      this.filter.status = s;
      this.reload();
    },
    reload() {
      this.page = 1;
      this.load();
    },
    onSearch() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => this.reload(), 500);
    },
    goPage(p) {
      this.page = p;
      this.load();
    },
    async load() {
      this.isLoading = true;
      try {
        const params = { page: this.page, limit: this.limit };
        if (this.filter.status) params.status = this.filter.status;
        if (this.filter.date_from) params.date_from = this.filter.date_from;
        if (this.filter.date_to) params.date_to = this.filter.date_to;
        if (this.filter.search) params.search = this.filter.search;
        const r = await axios.get("admin/public-orders", { params });
        const d = r.data.data || {};
        this.summary = d.summary || null;
        this.orders = d.orders || [];
        this.totalRecords = d.total_records || 0;
      } catch (err) {
        this.$toast.error("Gagal load: " + (err.response?.data?.message || err.message));
      } finally {
        this.isLoading = false;
      }
    },
    statusLabel(s) {
      if (s === "success") return "Sukses";
      if (s === "pending") return "Pending";
      if (s === "failed") return "Gagal";
      return s;
    },
    statusClass(s) {
      if (s === "success") return "badge badge-success";
      if (s === "pending") return "badge badge-warning text-white";
      if (s === "failed") return "badge badge-danger";
      return "badge badge-light";
    },
    formatRupiah(n) {
      const num = parseInt(n) || 0;
      return "Rp " + num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    formatTime(millis) {
      if (!millis) return "-";
      return new Date(parseInt(millis)).toLocaleString("id-ID", {
        timeZone: "Asia/Jakarta", day: "2-digit", month: "short", year: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    },
    async activateOrder(r) {
      const msg =
        `Aktifkan order #${r.id} (${r.name || r.email})?\n\n` +
        "Paket akan diaktifkan seperti pembayaran sukses (WhatsApp & email welcome jika order baru).";
      if (!window.confirm(msg)) return;
      this.actionBusy = r.id;
      try {
        await axios.post(`admin/public-orders/${r.id}/activate`);
        this.$toast.success("Order diaktifkan");
        await this.load();
      } catch (err) {
        this.$toast.error(err.response?.data?.message || err.message || "Gagal aktifkan");
      } finally {
        this.actionBusy = null;
      }
    },
    async deleteOrder(r) {
      let msg = `Hapus order #${r.id} (${r.name || r.email})?\n\nRecord transaksi akan dihapus dari sistem.`;
      if (r.status === "success") {
        msg +=
          "\n\nPERINGATAN: Order ini sudah SUKSES. Menghapus tidak membatalkan paket user yang sudah aktif.";
      }
      if (!window.confirm(msg)) return;
      this.actionBusy = r.id;
      try {
        await axios.delete(`admin/public-orders/${r.id}`);
        this.$toast.success("Order dihapus");
        await this.load();
      } catch (err) {
        this.$toast.error(err.response?.data?.message || err.message || "Gagal hapus");
      } finally {
        this.actionBusy = null;
      }
    },
    exportCSV() {
      const head = ["No", "Tanggal", "Nama", "Email", "Phone", "Paket", "Nominal", "Metode", "Status"];
      const lines = [head.join(",")];
      this.orders.forEach((r, i) => {
        const cols = [
          (this.page - 1) * this.limit + i + 1,
          `"${this.formatTime(r.created_at)}"`,
          `"${(r.name || "").replace(/"/g, "'")}"`,
          r.email || "", r.phone || "",
          `"${(r.package_name || "").replace(/"/g, "'")}"`,
          r.nominal || 0, r.method || "",
          r.status || "",
        ];
        lines.push(cols.join(","));
      });
      const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `order-publik-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>
